import {
  Box,
  Button,
  Container,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { ChangeEvent, FormEvent, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { FaPaperclip, FaRegCheckCircle } from "react-icons/fa";
import { FiAlertCircle, FiSend, FiX } from "react-icons/fi";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import Notification from "../../../components/ui/Notification";
import { submitJobApplication } from "../api";
import { JobAppForm } from "../types";

const MIN_NOMBRE_LENGTH = 2;
const MIN_COMENTARIO_LENGTH = 10;

interface Errors {
  nombre?: string;
  email?: string;
  comentario?: string;
  [key: string]: string | undefined;
}

interface Touched {
  nombre?: boolean;
  email?: boolean;
  comentario?: boolean;
  [key: string]: boolean | undefined;
}

export default function FormularioPostulacion() {
  const { t, i18n } = useTranslation();

  const [form, setForm] = useState<JobAppForm>({
    nombre: "",
    email: "",
    comentario: "",
    cv: null,
  });

  const [phoneValue, setPhoneValue] = useState<string>("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [cvAdjuntado, setCvAdjuntado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [token, setToken] = useState<string | null>(null);
  const captchaRef = useRef<ReCAPTCHA>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Touched>({});
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const isEmailValid = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const phoneError = () => {
    if (!phoneValue || phoneValue.trim() === "") {
      return t("contacto_form.error_telefono");
    }
    if (!isValidPhoneNumber(phoneValue)) {
      return t("contacto_form.error_telefono_formato");
    }
    return "";
  };

  const validateField = (name: string, value: string) => {
    let error = "";
    switch (name) {
      case "nombre":
        if (value.trim().length < MIN_NOMBRE_LENGTH)
          error = t("postulacion.error_nombre");
        break;
      case "email":
        if (!isEmailValid(value)) error = t("postulacion.error_email");
        break;
      case "comentario":
        if (value.trim().length < MIN_COMENTARIO_LENGTH)
          error = t("postulacion.error_comentario");
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    if (e.target instanceof HTMLInputElement && e.target.type === "file") {
      const files = e.target.files;
      if (files && files[0]) {
        setForm({ ...form, cv: files[0] });
        setCvAdjuntado(true);
      } else {
        setForm({ ...form, cv: null });
        setCvAdjuntado(false);
      }
    } else {
      let filteredValue = value;
      if (name === "nombre") {
        filteredValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, "");
      }
      setForm({ ...form, [name]: filteredValue });
      if (touched[name]) {
        validateField(name, filteredValue);
      }
    }
  };

  const handleRemoveFile = () => {
    setForm((prev) => ({ ...prev, cv: null }));
    setCvAdjuntado(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileName = (name: string | undefined) => {
    if (!name) return "";
    const maxLength = 20;
    if (name.length <= maxLength) return name;
    const extensionIndex = name.lastIndexOf(".");
    if (extensionIndex === -1) return `${name.substring(0, 15)}...`;
    const extension = name.substring(extensionIndex);
    const nameWithoutExt = name.substring(0, extensionIndex);
    return `${nameWithoutExt.substring(0, 5)}...${extension}`;
  };

  const isFormValid = () => {
    const pErr = phoneError();
    return (
      form.nombre.trim().length >= MIN_NOMBRE_LENGTH &&
      isEmailValid(form.email) &&
      form.comentario.trim().length >= MIN_COMENTARIO_LENGTH &&
      !pErr &&
      cvAdjuntado &&
      (recaptchaSiteKey ? token !== null : true) &&
      Object.values(errors).every((err) => !err)
    );
  };

  const onCaptchaChange = (value: string | null) => {
    setToken(value);
    if (mensaje) setMensaje("");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const currentErrors: Errors = {};
    if (form.nombre.trim().length < MIN_NOMBRE_LENGTH)
      currentErrors.nombre = t("postulacion.error_nombre");
    if (!isEmailValid(form.email))
      currentErrors.email = t("postulacion.error_email");
    if (form.comentario.trim().length < MIN_COMENTARIO_LENGTH)
      currentErrors.comentario = t("postulacion.error_comentario");

    const pErr = phoneError();
    if (pErr) setPhoneTouched(true);

    if (Object.keys(currentErrors).length > 0 || pErr) {
      setErrors(currentErrors);
      setTouched({ nombre: true, email: true, comentario: true });
      return;
    }

    if (recaptchaSiteKey && !token) {
      setMensaje(t("postulacion.captcha_required"));
      return;
    }

    setEnviando(true);
    setMensaje("");

    const formData = new FormData();
    formData.append("name", form.nombre.trim());
    formData.append("email", form.email.trim());
    formData.append("phone", phoneValue || "");
    formData.append("message", form.comentario.trim());
    if (form.cv) formData.append("cv", form.cv);
    if (recaptchaSiteKey) {
      formData.append("recaptchaToken", token || "");
    }

    try {
      await submitJobApplication(formData);
      setSuccessMessage(t("postulacion.exito"));
      setEnviado(true);
      setForm({ nombre: "", email: "", comentario: "", cv: null });
      setPhoneValue("");
      setPhoneTouched(false);
      setTouched({});
      setErrors({});
      setCvAdjuntado(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error: any) {
      console.error("error " + error);
      const baseMessage =
        error.friendlyMessage || t("postulacion.error_connection");
      setMensaje(baseMessage);
      if (captchaRef.current) captchaRef.current.reset();
      setToken(null);
    }
    setEnviando(false);
  };

  const currentPhoneError = phoneTouched ? phoneError() : "";

  const textFieldSlots = {
    root: {
      "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: "#ffffff",

        transition: "all 0.3s ease",
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#68a8de" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#68a8de",
        },
      },
    },
  };

  return (
    <Box
      component="section"
      id="formulario-postulacion"
      sx={{
        py: 10,
        bgcolor: "#f8fafc",
        borderTop: "1px solid #f1f5f9",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom, transparent, rgba(241, 245, 249, 0.5))",
          pointerEvents: "none",
        },
      }}
    >
      <Notification
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: "32px",
            boxShadow: "0 20px 50px rgba(10,17,40,0.06)",
            border: "1px solid rgba(226, 232, 240, 0.8)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "8px",
              background: "linear-gradient(to right, #0A1128, #68a8de)",
              zIndex: 10,
            }}
          />

          <Grid container>
            {/* Lado Izquierdo */}
            <Grid
              size={{ xs: 12, md: 5 }}
              sx={{
                bgcolor: "rgba(248, 250, 252, 0.5)",
                p: 5,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                borderBottom: { xs: "1px solid #f1f5f9", md: "none" },
                borderRight: { md: "1px solid #f1f5f9" },
              }}
            >
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: "#0A1128",
                    mb: 2,
                    lineHeight: 1.2,
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                  }}
                >
                  {t("postulacion.titulo")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#475569",
                    lineHeight: 1.6,
                    fontSize: "0.95rem",
                  }}
                >
                  {t("postulacion.subtitulo")}
                </Typography>
              </Box>
            </Grid>

            {/* Lado Derecho */}
            <Grid size={{ xs: 12, md: 7 }} sx={{ p: 5 }}>
              <Box
                component={motion.form}
                onSubmit={handleSubmit}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
              >
                <Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="nombre"
                    placeholder={t("postulacion.placeholder_nombre")}
                    value={form.nombre}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    slotProps={{
                      htmlInput: {
                        maxLength: 100,
                      },
                    }}
                    disabled={enviando || enviado}
                    error={Boolean(touched.nombre && errors.nombre)}
                    helperText={
                      touched.nombre && errors.nombre ? errors.nombre : ""
                    }
                    sx={textFieldSlots}
                  />
                </Box>

                <Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="email"
                    name="email"
                    placeholder={t("postulacion.placeholder_email")}
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    slotProps={{
                      htmlInput: {
                        maxLength: 100,
                      },
                    }}
                    disabled={enviando || enviado}
                    error={Boolean(touched.email && errors.email)}
                    helperText={
                      touched.email && errors.email ? errors.email : ""
                    }
                    sx={textFieldSlots}
                  />
                </Box>

                <Box>
                  <Box
                    sx={{
                      border: `1px solid ${currentPhoneError ? "#d32f2f" : "#ccc"}`,
                      borderRadius: "12px",
                      px: 2,
                      py: "10.5px",
                      bgcolor: "white",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: currentPhoneError ? "#d32f2f" : "#68a8de",
                      },
                      "&:focus-within": {
                        borderColor: currentPhoneError ? "#d32f2f" : "#68a8de",
                        boxShadow: "0 0 0 2px rgba(104, 168, 222, 0.2)",
                      },
                      "& .PhoneInput": {
                        display: "flex",
                        alignItems: "center",
                      },
                      "& .PhoneInputInput": {
                        width: "100%",
                        border: "none",
                        outline: "none",

                        fontSize: "1rem",
                        color: "#0A1128",
                        background: "transparent",
                      },
                    }}
                  >
                    <PhoneInput
                      international
                      defaultCountry="AR"
                      value={phoneValue}
                      onChange={(val) => setPhoneValue(val || "")}
                      onBlur={() => setPhoneTouched(true)}
                      disabled={enviando || enviado}
                      placeholder={t("postulacion.placeholder_telefono")}
                      maxLength={20}
                    />
                  </Box>
                  {currentPhoneError && (
                    <FormHelperText
                      error
                      sx={{
                        mx: 2,
                        mt: 0.5,
                      }}
                    >
                      {currentPhoneError}
                    </FormHelperText>
                  )}
                </Box>

                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    name="comentario"
                    placeholder={t("postulacion.placeholder_comentario")}
                    value={form.comentario}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    slotProps={{
                      htmlInput: {
                        maxLength: 1000,
                      },
                    }}
                    disabled={enviando || enviado}
                    error={Boolean(touched.comentario && errors.comentario)}
                    helperText={
                      touched.comentario && errors.comentario
                        ? errors.comentario
                        : ""
                    }
                    sx={textFieldSlots}
                  />
                </Box>

                {!enviado && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "center", md: "flex-start" },
                      mt: 0.5,
                    }}
                  >
                    {recaptchaSiteKey ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          width: "100%",
                          "& .recaptcha-container": {
                            transform: {
                              xs: "scale(0.85)",
                              sm: "scale(1)",
                            },
                            transformOrigin: "center",
                          },
                        }}
                      >
                        <Box className="recaptcha-container">
                          <ReCAPTCHA
                            ref={captchaRef}
                            sitekey={recaptchaSiteKey}
                            hl={i18n.language}
                            key={i18n.language}
                            onChange={onCaptchaChange}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="error">
                        {t("postulacion.captcha_not_configured")}
                      </Typography>
                    )}
                  </Box>
                )}

                {mensaje && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      border: "1px solid",
                      bgcolor: enviado ? "#ecfdf5" : "#fef2f2",
                      borderColor: enviado ? "#a7f3d0" : "#fca5a5",
                      color: enviado ? "#065f46" : "#991b1b",
                    }}
                  >
                    {/* FiAlertCircle de react-icons */}
                    {enviado ? (
                      <FaRegCheckCircle size={18} />
                    ) : (
                      <FiAlertCircle size={18} />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      {mensaje}
                    </Typography>
                  </Box>
                )}

                {cvAdjuntado && !enviado && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      px: 2,
                      py: 1,
                      borderRadius: "12px",
                      alignSelf: "flex-start",
                    }}
                  >
                    {/* FaPaperclip de react-icons */}
                    <FaPaperclip size={14} style={{ color: "#64748b" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#334155",
                      }}
                    >
                      {t("postulacion.cv_adjunto")}:{" "}
                      <strong>{formatFileName(form.cv?.name)}</strong>
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={handleRemoveFile}
                      disabled={enviando}
                      sx={{
                        background: "transparent",
                        border: "none",
                        color: "#ef4444",
                        display: "flex",
                        cursor: "pointer",
                        p: 0.5,
                        borderRadius: "50%",
                        "&:hover": { bgcolor: "#fef2f2" },
                      }}
                    >
                      {/* FiX de react-icons */}
                      <FiX size={14} />
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    pt: 1,
                  }}
                >
                  <Button
                    component="label"
                    variant="contained"
                    disabled={enviando || enviado}
                    startIcon={<FaPaperclip size={16} />}
                    sx={{
                      flex: 1,
                      py: 1.8,
                      borderRadius: "12px",

                      fontWeight: 600,
                      textTransform: "none",
                      bgcolor: "#059669",
                      "&:hover": { bgcolor: "#047857" },
                    }}
                  >
                    {t("postulacion.adjuntar_cv")}
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="cv"
                      accept=".pdf,.doc,.docx,.txt,.html"
                      onChange={handleChange}
                      required
                      hidden
                    />
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!isFormValid() || enviando || enviado}
                    startIcon={<FiSend size={16} />}
                    sx={{
                      flex: 1,
                      py: 1.8,
                      borderRadius: "12px",

                      fontWeight: 600,
                      textTransform: "none",
                      bgcolor: "#0A1128",
                      "&:hover": { bgcolor: "#121c3e" },
                      "&:disabled": {
                        bgcolor: "rgba(10, 17, 40, 0.4)",
                        color: "rgba(255,255,255,0.7)",
                      },
                    }}
                  >
                    {enviando
                      ? t("postulacion.enviando")
                      : enviado
                        ? t("postulacion.enviado")
                        : t("postulacion.boton")}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
